import { SimSettingsFormValues } from '@features/Mobile/types';
import { MobileTariff, Porting, Sim } from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';
import { BooleanSelection } from '@src/types/index';

import { defaultPortingValues, defaultValues } from '../constants';
import { isPortingPac, isPortingStac } from '../utils';

const formatPortingRequirement = (
  porting: Nullable<Porting>
): Omit<SimSettingsFormValues, 'budgetControl' | 'simPlanId'> => {
  if (!porting) {
    return defaultPortingValues;
  }

  if (isPortingPac(porting)) {
    return {
      isKeepNumber: BooleanSelection.Yes,
      keepNumber: porting.mobileNumber,
      pacNumber: porting.pac,
      isCancelNumber: BooleanSelection.No,
      cancelNumber: '',
      stacNumber: '',
    };
  }

  if (isPortingStac(porting)) {
    return {
      isKeepNumber: BooleanSelection.No,
      keepNumber: '',
      pacNumber: '',
      isCancelNumber: BooleanSelection.Yes,
      cancelNumber: porting.mobileNumber,
      stacNumber: porting.stac,
    };
  }

  return defaultPortingValues;
};

const getInitialValues = (
  editedSim: Nullable<string>,
  selectedSims: Sim[]
): SimSettingsFormValues => {
  // Every quote which is not selected should have empty requirements
  if (!editedSim) {
    return defaultValues;
  }

  // Fetching the basket and setting the active quote ID is now
  // asynchronous, and as we may set the quote ID before the basket
  // query has returned, just return the default values until they
  // can be updated at a later point.
  if (selectedSims.length === 0) {
    return defaultValues;
  }

  const editedQuote = selectedSims.find((sim) => sim.id === editedSim);

  if (!editedQuote) {
    return defaultValues;
  }

  const {
    id,
    settings: { budgetControl, porting },
  } = editedQuote;

  return {
    simPlanId: id,
    budgetControl: budgetControl ? BooleanSelection.Yes : BooleanSelection.No,
    ...formatPortingRequirement(porting ?? null),
  };
};

const swapSimTariff = (
  simId: string,
  selectedSims: Sim[],
  availableTariffs: MobileTariff[]
) => {
  const sim = selectedSims.find(({ id }) => id === simId);

  if (!sim) {
    return availableTariffs;
  }

  const simTariff = sim.selectedTariff;
  const simTariffType = simTariff.tariffType;

  return availableTariffs.reduce((tariffsAcc, tariff) => {
    const { tariffType } = tariff;

    if (tariffType === simTariffType) {
      tariffsAcc.push(simTariff);
    } else {
      tariffsAcc.push(tariff);
    }

    return tariffsAcc;
  }, [] as MobileTariff[]);
};

const useSettingsFormState = (
  selectedSims: Sim[],
  availableTariffs: MobileTariff[]
) => {
  // Describes the quote that is currently being edited
  const [editedSim, setEditedSim] = useSyncState<Nullable<string>>(
    'edited-sim-quote-id',
    null
  );

  const initialValues = getInitialValues(editedSim, selectedSims);
  let mobileTariffs = availableTariffs;

  if (editedSim) {
    // Edited sim has to be included in the list of available tariffs
    mobileTariffs = swapSimTariff(editedSim, selectedSims, availableTariffs);
  }

  return {
    mobileTariffs,
    editedSim,
    setEditedSim,
    initialValues,
  };
};

export { useSettingsFormState };
